/**
 * iter 50 — Google Places Autocomplete input.
 *
 * Drop-in text input that, when the user picks a suggestion, calls
 * `onPlaceSelected({ address1, address2, city, state, postal_code,
 * latitude, longitude, formatted_address })`. Works with our standard
 * `.input` class for visual consistency.
 *
 * Notes
 *  • Falls back to a plain input if the SDK fails to load (e.g. no API key).
 *  • Use `inputRef.current.value` to read the raw text; the parent should
 *    keep its own state synced via `onChange` so the user can type freely
 *    BEFORE picking a Google suggestion (manual entry remains possible).
 */
import { useEffect, useRef } from "react";
import { useGoogleMaps } from "../lib/useGoogleMaps";

function parsePlace(place) {
  const out = {
    address1: "", address2: "", city: "", state: "",
    postal_code: "", latitude: null, longitude: null,
    formatted_address: place.formatted_address || "",
  };
  let streetNumber = "", route = "";
  for (const c of place.address_components || []) {
    const types = c.types || [];
    if (types.includes("street_number")) streetNumber = c.long_name;
    else if (types.includes("route")) route = c.short_name;
    else if (types.includes("locality")) out.city = c.long_name;
    else if (!out.city && types.includes("postal_town")) out.city = c.long_name;
    else if (!out.city && types.includes("sublocality")) out.city = c.long_name;
    else if (types.includes("administrative_area_level_1")) out.state = c.short_name;
    else if (types.includes("postal_code")) out.postal_code = c.long_name;
    else if (types.includes("subpremise")) out.address2 = c.long_name;
  }
  out.address1 = [streetNumber, route].filter(Boolean).join(" ").trim();
  if (place.geometry?.location) {
    out.latitude = place.geometry.location.lat();
    out.longitude = place.geometry.location.lng();
  }
  return out;
}

export default function GooglePlacesAutocomplete({
  value, onChange, onPlaceSelected, placeholder = "Start typing an address…",
  testid, className = "",
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const { ready, error } = useGoogleMaps();

  // iter 52 — pin the latest onPlaceSelected callback in a ref so we don't
  // need to include it in the effect's dependency array. Without this the
  // parent's inline arrow function changes on every render, the effect
  // re-runs, the listener is destroyed mid-click, and the user has to click
  // the suggestion twice for the change event to fire on a fresh listener.
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  useEffect(() => { onPlaceSelectedRef.current = onPlaceSelected; }, [onPlaceSelected]);

  useEffect(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: ["us", "ca"] },
      fields: ["address_components", "geometry", "formatted_address"],
    });
    autocompleteRef.current = ac;
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place || !place.address_components) return;
      const parsed = parsePlace(place);
      onPlaceSelectedRef.current?.(parsed);
    });
    return () => {
      if (listener) window.google.maps.event.removeListener(listener);
      autocompleteRef.current = null;
    };
  }, [ready]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        className={`input ${className}`}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={ready ? placeholder : (error ? "Address autocomplete unavailable" : "Loading…")}
        autoComplete="off"
        data-testid={testid || "google-places-input"}
      />
      {error && (
        <div className="text-[10px] text-destructive mt-1" data-testid={testid ? `${testid}-error` : undefined}>
          {error}
        </div>
      )}
    </div>
  );
}
